import {
  Box,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import React, { useRef, useState } from "react";
import Input from "../../../../components/Input";
import ButtonComp from "../../../../components/Button";
import trash from "../../../../assets/images/trash.svg";
import eye from "../../../../assets/images/eye.svg";
import { useUserStore } from "../../../../store/useUserStore";
import { useCaseCreationStore } from "../../../../store/useCaseCreationStore";
import { usePolicyStore } from "../../../../store/usePolicyStore";
import { caseSubmit } from "../../../../services/case-services";
import { useToast } from "../../../../hooks/useToast";
import { useOtpVerifiedStore } from "../../../../store/useOtpVerifiedStore";
import { useAuthStore } from "../../../../store/useAuthStore";
import { useSessionStore } from "../../../../store/useSessionStore";
import {
  caseActionBarSx,
  caseAddButtonSx,
  caseButtonSx,
  caseEmptyCellSx,
  caseFormCardSx,
  caseGridSx,
  caseLabelSx,
  caseTableContainerSx,
  caseTableHeadSx,
  caseUploadBoxSx,
} from "./caseFormStyles";

interface DocumentRow {
  id: number;
  documentKey: string;
  documentName: string;
  documentType: string;
  uploadedBy: string;
  date: string;
  file: File;
}

const DOCUMENT_OPTIONS = [
  {
    key: "claimant_id",
    label: "Informant ID",
    required: false,
  },
  {
    key: "deceased_id",
    label: "Deceased ID",
    required: false,
  },
  {
    key: "dha_1680_home_death_form",
    label: "Declaration of death from paramedics",
    required: false,
  },
  {
    key: "dha_1663_form",
    label: "BI Document",
    required: false,
  },
];

export default function Documents({
  onBack,
  onNext,
  minimalCapture = false,
  hideUploader = false,
  beforeSubmit,
}: any) {
  const [rows, setRows] = useState<DocumentRow[]>([]);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [documentType, setDocumentType] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const submitLockRef = useRef(false);

  const role = useUserStore((s) => s.role);
  const { userRole, userName, userIdData } = useSessionStore();
  const effectiveRole = userRole || role;
  const { setIsOtpVerified } = useOtpVerifiedStore();
  const { userId, first_name, last_name, user_name } = useAuthStore();
  const currentUserId = userId || userIdData;
  const currentUserName =
    userName ||
    `${first_name || ""} ${last_name || ""}`.trim() ||
    user_name ||
    effectiveRole ||
    "System User";

  const { showInfoToast } = useToast();

  const {
    informantDetails,
    deceasedDetails,
    deceasedLocation,
    mortuaryName,
    documents,
    packageSelection,
    policyNumber,
    selectedLivesInsured,
    setDocuments,
    resetCase,
  } = useCaseCreationStore();
  const policyData = usePolicyStore((state) => state.policyData);
  const setPolicyData = usePolicyStore((state) => state.setPolicyData);

  const resolvePolicyNumber = () =>
    (
      policyNumber ||
      policyData?.policy_number ||
      policyData?.policyNumber ||
      policyData?.mainMember?.policyNumber ||
      policyData?.MainMember?.Policy_Number ||
      policyData?.MainMember?.policyNumber ||
      ""
    ).trim();

  const handleAdd = () => {
    if (!selectedFile || !documentType) return;

    const selectedDocument = DOCUMENT_OPTIONS.find(
      (option) => option.key === documentType,
    );

    const newRow: DocumentRow = {
      id: Date.now(),
      documentKey: documentType,
      documentName: selectedFile.name,
      documentType: selectedDocument?.label || documentType,
      uploadedBy: role,
      date: new Date().toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      }),
      file: selectedFile,
    };

    setRows((prev) => {
      const filteredRows = prev.filter((row) => row.documentKey !== documentType);
      return [...filteredRows, newRow];
    });

    setDocuments({
      [documentType]: selectedFile,
    });

    setSelectedFile(null);
    setDocumentType("");
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleDelete = (id: number) => {
    const selectedRow = rows.find((row) => row.id === id);
    if (!selectedRow) return;

    setRows((prev) => prev.filter((row) => row.id !== id));

    setDocuments({
      [selectedRow.documentKey]: null,
    });
  };

  const handlePreview = (file: File) => {
    const fileURL = URL.createObjectURL(file);
    window.open(fileURL);
  };

  const handleSubmit = async () => {
    if (submitLockRef.current) return;

    if (beforeSubmit && !beforeSubmit()) return;

    const sourceType = minimalCapture ? "MANUAL" : packageSelection.sourceType || "MANUAL";
    const resolvedPolicyNumber = resolvePolicyNumber();

    if (!mortuaryName?.trim()) {
      showInfoToast("Mortuary name is required before creating the case.");
      return;
    }

    if (sourceType === "POLICY" && !selectedLivesInsured) {
      showInfoToast("Please select a lives insured member before submitting the case.");
      return;
    }

    if (sourceType === "POLICY" && !resolvedPolicyNumber) {
      showInfoToast("Please load the policy before submitting the scheme case.");
      return;
    }

    // State updates are asynchronous, so use a ref as the immediate lock as well.
    // This prevents a fast double-click from creating the case once and then
    // showing a duplicate-deceased error for the second request.
    submitLockRef.current = true;
    setIsSubmitting(true);

    const formData = new FormData();

    const normalizedClaimant = {
      ...informantDetails,
      contactNumber: `${informantDetails.countryCode || "+27"}${informantDetails.contactNumber || ""}`,
    };

    const quotationLineItems = (minimalCapture ? [] : packageSelection.lineItems ?? []).map((li) => ({
      quotableItemCode: li.code,
      quantity: li.quantity,
      unitPrice: li.unitPrice,
    }));

    const casePayload = {
      userId: currentUserId,
      createdByRole: effectiveRole,
      createdByName: currentUserName,
      claimant: normalizedClaimant,
      deceased: deceasedDetails,
      deceasedLocation: deceasedLocation,
      mortuaryName: mortuaryName.trim(),
      funeralPackageSourceType: sourceType,
      quotationLineItems,
      policyNumber: sourceType === "POLICY" ? resolvedPolicyNumber : undefined,
    };

    formData.append("case", JSON.stringify(casePayload));

    Object.entries(documents).forEach(([key, file]) => {
      if (file) {
        formData.append(key, file as File);
      }
    });

    try {
      const response = await caseSubmit(formData);
      if (response.status === 200) {
        setIsOtpVerified(false);
        onNext();
        resetCase();
        setPolicyData(null);
      } else {
        showInfoToast(response.data.message);
      }
    } catch (error) {
      console.error("Error creating case:", error);
    } finally {
      submitLockRef.current = false;
      setIsSubmitting(false);
    }
  };

  return (
    <Paper elevation={0} sx={caseFormCardSx}>
      {!hideUploader && <>
        <Grid container spacing={2.25} sx={caseGridSx}>
        <Grid size={{ xs: 12, md: 5 }}>
          <Typography sx={caseLabelSx}>Document Type</Typography>

          <Input
            select
            fullWidth
            value={documentType}
            onChange={(e: any) => setDocumentType(e.target.value)}
          >
            <MenuItem value="">Select Document Type</MenuItem>
            {DOCUMENT_OPTIONS.map((option) => (
              <MenuItem key={option.key} value={option.key}>
                {option.label}
              </MenuItem>
            ))}
          </Input>
        </Grid>

        <Grid size={{ xs: 12, md: 5 }}>
          <Typography sx={caseLabelSx}>Document</Typography>

          <Box sx={caseUploadBoxSx}>
            <ButtonComp
              variant="contained"
              sx={{
                backgroundColor: "#6D6D6D",
                borderRadius: "8px",
                height: 36,
                minWidth: 100,
                mr: 2,
                textTransform: "none",
              }}
              onClick={() => fileInputRef.current?.click()}
            >
              Choose
            </ButtonComp>

            <Typography
              sx={{
                color: "#6B7280",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                minWidth: 0,
              }}
            >
              {selectedFile ? selectedFile.name : "No file chosen"}
            </Typography>

            <input
              type="file"
              hidden
              ref={fileInputRef}
              onChange={(e) =>
                setSelectedFile(e.target.files ? e.target.files[0] : null)
              }
            />
          </Box>
        </Grid>

        <Grid size={{ xs: 12, md: 2 }}>
          <Box mt={{ xs: 0, md: 2.85 }}>
            <ButtonComp
              variant="contained"
              onClick={handleAdd}
              sx={caseAddButtonSx}
              disabled={!documentType || !selectedFile}
            >
              Add
            </ButtonComp>
          </Box>
        </Grid>
        </Grid>

        <TableContainer component={Paper} elevation={0} sx={caseTableContainerSx}>
        <Table>
          <TableHead sx={caseTableHeadSx}>
            <TableRow>
              <TableCell sx={{ fontWeight: 700 }}>Document Name</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Document Type</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Uploaded By</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Date</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Action</TableCell>
            </TableRow>
          </TableHead>

          <TableBody sx={{ backgroundColor: "#ffffff" }}>
            {rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  <Typography sx={caseEmptyCellSx}>
                    No documents added yet
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row) => (
                <TableRow key={row.id}>
                  <TableCell>{row.documentName}</TableCell>
                  <TableCell>{row.documentType}</TableCell>
                  <TableCell>{row.uploadedBy}</TableCell>
                  <TableCell>{row.date}</TableCell>
                  <TableCell>
                    <IconButton onClick={() => handlePreview(row.file)}>
                      <img src={eye} alt="eye" width={20} />
                    </IconButton>

                    <IconButton onClick={() => handleDelete(row.id)}>
                      <img src={trash} alt="trash" width={18} />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        </TableContainer>
      </>}

      <Box sx={caseActionBarSx}>
        <ButtonComp variant="outlined" onClick={onBack} sx={caseButtonSx} disabled={isSubmitting}>
          Back
        </ButtonComp>

        <ButtonComp
          variant="contained"
          onClick={handleSubmit}
          sx={caseButtonSx}
          disabled={isSubmitting}
        >
          {isSubmitting
            ? "Saving case..."
            : minimalCapture
              ? "Save Initial Case"
              : "Submit"}
        </ButtonComp>
      </Box>
    </Paper>
  );
}
